import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Link } from "react-router-dom";
import {
  Mail,
  Phone,
  Send,
  MessageSquare,
  User,
  Building,
  ArrowRight,
  HelpCircle,
  ShieldCheck,
  Zap,
  ChevronRight,
  AlertCircle,
  Loader2,
  Facebook,
  Twitter,
  Linkedin,
  Instagram,
  Clock as ClockIcon,
  CheckCircle,
  MapPin,
} from "lucide-react";
import { messageService } from "../api/services/messageService";

const Contact = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    subject: "",
    content: "",
  });

  const [formStatus, setFormStatus] = useState({
    isSubmitting: false,
    isSubmitted: false,
    isError: false,
    message: "",
  });

  const [focusedField, setFocusedField] = useState(null);

  const contactInfo = [
    {
      icon: Phone,
      title: "Phone Number",
      value: "+20-111-477-9577",
      href: "tel:+201114779577",
      description: "Available Mon-Fri, 9AM-6PM",
    },
    {
      icon: Mail,
      title: "Email Address",
      value: "admin@tradebridge-export.com",
      href: "mailto:admin@tradebridge-export.com",
      description: "Main contact for general inquiries",
    },
    {
      icon: MapPin,
      title: "Office Location",
      value: "Alexandria, Egypt",
      href: "#",
      description: "Global trade hub",
    },
    {
      icon: ClockIcon,
      title: "Business Hours",
      value: "9:00 AM - 6:00 PM",
      href: "#",
      description: "Sunday - Thursday",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    setFormStatus({
      isSubmitting: true,
      isSubmitted: false,
      isError: false,
      message: "",
    });

    try {
      const messageData = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        company: formData.company,
        subject: formData.subject || "General Inquiry",
        content: formData.content,
        category: "general",
        priority: "normal",
      };

      await messageService.createMessage(messageData);

      setFormStatus({
        isSubmitting: false,
        isSubmitted: true,
        isError: false,
        message: "Thank you! We'll get back to you within 24 hours.",
      });

      setFormData({
        name: "",
        email: "",
        phone: "",
        company: "",
        subject: "",
        content: "",
      });

      setTimeout(() => {
        setFormStatus((prev) => ({ ...prev, isSubmitted: false }));
      }, 5000);
    } catch (error) {
      console.error("Error sending message:", error);
      setFormStatus({
        isSubmitting: false,
        isSubmitted: false,
        isError: true,
        message:
          error.response?.data?.message ||
          "Failed to send message. Please try again or call us directly.",
      });
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 border border-gray-200 dark:border-gray-900 rounded-2xl shadow-lg">
      {/* Blur Background */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-[40%] -left-[20%] w-[80%] h-[80%] bg-gradient-to-br from-primary-200/20 to-secondary-200/20 dark:from-primary-900/20 dark:to-secondary-900/20 rounded-full blur-3xl animate-pulse" />
        <div className="absolute -bottom-[40%] -right-[20%] w-[80%] h-[80%] bg-gradient-to-tl from-primary-300/10 to-secondary-300/10 dark:from-primary-800/10 dark:to-secondary-800/10 rounded-full blur-3xl animate-pulse delay-1000" />
      </div>

      <div className="relative container mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="text-center max-w-3xl mx-auto mb-10 sm:mb-16"
        >
          <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-full border border-white/30 dark:border-gray-800/30 shadow-sm mb-4">
            <MessageSquare className="w-3 h-3 sm:w-4 sm:h-4 text-primary-500" />
            <span className="text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300">
              Get in Touch
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4">
            <span className="text-gray-900 dark:text-white">Let's Start a</span>
            <br />
            <span className="bg-gradient-to-r from-primary-600 to-secondary-600 bg-clip-text text-transparent">
              Conversation
            </span>
          </h1>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400">
            Reach out to us through any of the channels below. We're here to
            help you succeed.
          </p>
        </motion.div>

        {/* Contact Cards */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-12 sm:mb-16"
        >
          {contactInfo.map((item, i) => {
            const Icon = item.icon;
            return (
              <motion.a
                key={i}
                href={item.href}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-white/30 dark:border-gray-800/30 hover:shadow-xl transition-all cursor-pointer"
              >
                <div className="w-12 h-12 rounded-xl bg-gradient-to-r from-primary-500 to-secondary-500 flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-sm font-semibold text-gray-900 dark:text-white mb-1">
                  {item.title}
                </h3>
                <p className="text-sm text-primary-600 dark:text-primary-400 font-medium mb-2">
                  {item.value}
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {item.description}
                </p>
              </motion.a>
            );
          })}
        </motion.div>

        {/* Main Content - Form & Info */}
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-6 sm:gap-8">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl sm:rounded-3xl border border-white/30 dark:border-gray-800/30 shadow-xl overflow-hidden"
            >
              <div className="p-6 sm:p-8">
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-2 bg-gradient-to-r from-primary-500/10 to-secondary-500/10 rounded-xl">
                    <Send className="w-5 h-5 text-primary-600" />
                  </div>
                  <div>
                    <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">
                      Send a Message
                    </h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      Fill out the form and we'll respond within 24 hours
                    </p>
                  </div>
                </div>

                <AnimatePresence mode="wait">
                  {formStatus.isSubmitted ? (
                    <motion.div
                      key="success"
                      initial={{ opacity: 0, scale: 0.95 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.95 }}
                      className="text-center py-8"
                    >
                      <div className="relative inline-block mb-4">
                        <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full blur-xl opacity-50" />
                        <div className="relative w-16 h-16 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl flex items-center justify-center">
                          <CheckCircle className="w-8 h-8 text-white" />
                        </div>
                      </div>
                      <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
                        Message Sent!
                      </h3>
                      <p className="text-sm text-gray-600 dark:text-gray-400">
                        {formStatus.message}
                      </p>
                    </motion.div>
                  ) : (
                    <motion.form
                      key="form"
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      exit={{ opacity: 0 }}
                      onSubmit={handleSubmit}
                      className="space-y-4"
                    >
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Full Name <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <User
                              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                                focusedField === "name"
                                  ? "text-primary-600"
                                  : "text-gray-400"
                              }`}
                            />
                            <input
                              type="text"
                              name="name"
                              value={formData.name}
                              onChange={handleChange}
                              onFocus={() => setFocusedField("name")}
                              onBlur={() => setFocusedField(null)}
                              required
                              className="w-full pl-10 pr-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                              placeholder="John Doe"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Email Address{" "}
                            <span className="text-red-500">*</span>
                          </label>
                          <div className="relative">
                            <Mail
                              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                                focusedField === "email"
                                  ? "text-primary-600"
                                  : "text-gray-400"
                              }`}
                            />
                            <input
                              type="email"
                              name="email"
                              value={formData.email}
                              onChange={handleChange}
                              onFocus={() => setFocusedField("email")}
                              onBlur={() => setFocusedField(null)}
                              required
                              className="w-full pl-10 pr-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                              placeholder="john@company.com"
                            />
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Phone Number
                          </label>
                          <div className="relative">
                            <Phone
                              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                                focusedField === "phone"
                                  ? "text-primary-600"
                                  : "text-gray-400"
                              }`}
                            />
                            <input
                              type="tel"
                              name="phone"
                              value={formData.phone}
                              onChange={handleChange}
                              onFocus={() => setFocusedField("phone")}
                              onBlur={() => setFocusedField(null)}
                              className="w-full pl-10 pr-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                              placeholder="+20-111-477-9577"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                            Company Name
                          </label>
                          <div className="relative">
                            <Building
                              className={`absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 ${
                                focusedField === "company"
                                  ? "text-primary-600"
                                  : "text-gray-400"
                              }`}
                            />
                            <input
                              type="text"
                              name="company"
                              value={formData.company}
                              onChange={handleChange}
                              onFocus={() => setFocusedField("company")}
                              onBlur={() => setFocusedField(null)}
                              className="w-full pl-10 pr-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                              placeholder="Your Company"
                            />
                          </div>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Subject
                        </label>
                        <input
                          type="text"
                          name="subject"
                          value={formData.subject}
                          onChange={handleChange}
                          className="w-full px-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all text-sm"
                          placeholder="What's this about?"
                        />
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Message <span className="text-red-500">*</span>
                        </label>
                        <textarea
                          name="content"
                          value={formData.content}
                          onChange={handleChange}
                          required
                          rows={4}
                          className="w-full px-3 py-2.5 bg-white/50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 focus:border-primary-500 transition-all resize-none text-sm"
                          placeholder="Please describe how we can help you..."
                        />
                      </div>

                      {formStatus.isError && (
                        <motion.div
                          initial={{ opacity: 0, y: -10 }}
                          animate={{ opacity: 1, y: 0 }}
                          className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl"
                        >
                          <AlertCircle
                            size={16}
                            className="text-red-600 dark:text-red-400 flex-shrink-0"
                          />
                          <p className="text-xs text-red-600 dark:text-red-400">
                            {formStatus.message}
                          </p>
                        </motion.div>
                      )}

                      <button
                        type="submit"
                        disabled={formStatus.isSubmitting}
                        className="relative w-full py-3 bg-gradient-to-r from-primary-600 to-secondary-600 text-white font-semibold rounded-xl overflow-hidden group disabled:opacity-70 disabled:cursor-not-allowed"
                      >
                        <span className="relative z-10 flex items-center justify-center gap-2 text-sm">
                          {formStatus.isSubmitting ? (
                            <>
                              <Loader2 size={16} className="animate-spin" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send size={16} />
                              Send Message
                              <ArrowRight
                                size={14}
                                className="group-hover:translate-x-0.5 transition-transform"
                              />
                            </>
                          )}
                        </span>
                      </button>

                      <div className="flex items-center justify-center gap-3 pt-2">
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <ShieldCheck size={12} />
                          <span>Secure</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300 dark:bg-gray-700" />
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <ClockIcon size={12} />
                          <span>24hr Response</span>
                        </div>
                        <div className="w-px h-3 bg-gray-300 dark:bg-gray-700" />
                        <div className="flex items-center gap-1.5 text-xs text-gray-500">
                          <CheckCircle size={12} />
                          <span>SSL Encrypted</span>
                        </div>
                      </div>
                    </motion.form>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>

            {/* Information Panel */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              className="space-y-5"
            >
              {/* Quick Response Card */}
              <div className="bg-gradient-to-br from-primary-500 to-secondary-500 rounded-2xl p-6 text-white overflow-hidden relative">
                <div className="absolute inset-0 opacity-10">
                  <div
                    className="absolute inset-0"
                    style={{
                      backgroundImage:
                        "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
                      backgroundSize: "20px 20px",
                    }}
                  />
                </div>
                <div className="relative">
                  <div className="flex items-center gap-2 mb-3">
                    <div className="p-2 bg-white/10 rounded-xl">
                      <Zap className="w-5 h-5" />
                    </div>
                    <h3 className="font-semibold text-lg">Quick Response</h3>
                  </div>
                  <p className="text-white/90 text-sm mb-4">
                    We typically respond to all inquiries within 24 hours. For
                    urgent matters, please call us directly.
                  </p>
                  <div className="flex items-center gap-2 text-sm">
                    <ClockIcon size={14} />
                    <span>Response Time: &lt; 24 hours</span>
                  </div>
                </div>
              </div>

              {/* FAQ Preview */}
              <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-white/30 dark:border-gray-800/30">
                <div className="flex items-center gap-2 mb-4">
                  <HelpCircle size={18} className="text-primary-600" />
                  <h3 className="font-semibold text-gray-900 dark:text-white">
                    Frequently Asked Questions
                  </h3>
                </div>
                <div className="space-y-3">
                  {[
                    "How quickly can I get a quote?",
                    "Do you offer sample products?",
                    "What payment terms are available?",
                    "How does shipping work?",
                  ].map((question, i) => (
                    <div key={i} className="group cursor-pointer">
                      <p className="text-sm text-gray-700 dark:text-gray-300 group-hover:text-primary-600 transition-colors">
                        {question}
                      </p>
                    </div>
                  ))}
                </div>
                <Link
                  to="/support"
                  className="mt-4 inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium"
                >
                  View All FAQs <ChevronRight size={14} />
                </Link>
              </div>

              {/* Connect Section */}
              <div className="bg-white/40 dark:bg-gray-900/40 backdrop-blur-xl rounded-2xl p-5 border border-white/30 dark:border-gray-800/30">
                <h3 className="font-semibold text-gray-900 dark:text-white mb-3 text-sm">
                  Connect With Us
                </h3>
                <div className="flex gap-2">
                  {[
                    {
                      icon: Facebook,
                      href: "https://facebook.com/share/1cPzsK8Pv5",
                      label: "Facebook",
                    },
                    { icon: Twitter, href: "https://x.com", label: "Twitter" },
                    {
                      icon: Linkedin,
                      href: "https://www.linkedin.com/in/trade-bridge-export-253551432",
                      label: "LinkedIn",
                    },
                    {
                      icon: Instagram,
                      href: "https://www.instagram.com/tradebridge.export",
                      label: "Instagram",
                    },
                  ].map((social, index) => {
                    const Icon = social.icon;
                    return (
                      <a
                        key={index}
                        href={social.href}
                        aria-label={social.label}
                        className="w-9 h-9 bg-gray-100 dark:bg-gray-800 rounded-lg flex items-center justify-center text-gray-600 dark:text-gray-400 hover:bg-primary-600 hover:text-white transition-all duration-300"
                      >
                        <Icon size={16} />
                      </a>
                    );
                  })}
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Footer Note */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
          className="text-center mt-12 pt-6 border-t border-gray-200 dark:border-gray-800"
        >
          <p className="text-xs text-gray-500 dark:text-gray-400">
            Trade Bridge Export — Connecting global buyers with trusted
            suppliers
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default Contact;
